<template>
    <page :busy="busy">
        <grid
            ref="grid"
            rowSelection="multiple"
            @selection-changed="handleSelectionChange"
            :suppress-row-click-selection="true"
            :columnDefs="columnDefs"
            :getRowStyle="rowStyle"
            :fetchCallback="fetchCallback"
            :isAutoWidth="true"
            :pinnedBottomRowData="pinnedBottomRowData"
            @on-total-records="setTotalRecords"
        >
            <responsive-dropdown
                id="guest-list-filters"
                :icon="['fas', 'filter']"
                :items="dropdownFilterOptions"
                variant="primary"
                v-if="!hasSelectedRows"
            >
                {{ filter.label }}
            </responsive-dropdown>
            <responsive-button
                :icon="['fas', 'xmark']"
                @click="clearSelection()"
                variant="primary"
                v-if="hasSelectedRows"
            >
                Clear Selection
            </responsive-button>
            <responsive-dropdown
                id="guest-list-filters"
                :icon="['fas', 'message']"
                :items="actionOptions"
                variant="primary"
                v-if="actionOptions.length > 0"
            >
                Communication Actions
            </responsive-dropdown>
            <b-button-group aria-label="Export table data" v-if="!hasSelectedRows">
                <responsive-button
                    :icon="['fas', 'file-excel']"
                    :href="exportExcelHref"
                    @click="generatingExcelReportSuccessToast"
                    variant="primary"
                ></responsive-button>
            </b-button-group>
            <responsive-button
                :icon="['fas', 'rotate']"
                @click="refreshGrid()"
                variant="primary"
                v-if="!hasSelectedRows"
            >
                Refresh
            </responsive-button>
            <responsive-button :icon="['fas', 'plus']" href="/app/guest/new/" variant="primary" v-if="!hasSelectedRows">
                Add Guest
            </responsive-button>
        </grid>
    </page>
</template>

<script>
    import API from '../../../api';
    import Page from '../../generic/page/page.vue';
    import Grid from '../../generic/grid/grid.vue';
    import { escapeHTML } from '../../../utils/strings';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import { isMediumScreen } from '../../../utils/screens';
    import { mapMutations } from 'vuex';

    export default {
        name: 'GuestList',
        components: {
            Page,
            Grid,
            ResponsiveButton,
            ResponsiveDropdown,
            actionsCellRenderer: ActionsCellRenderer,
        },
        mixins: [removeScrollMixin],
        beforeMount() {
            this.rowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
        },
        mounted() {
            this.fetchCommunicationActionPermissions();
            this.$store.dispatch('guest/setDetailsTabTitle');
            this.$store.dispatch('guest/setGuest', null);
            this.$store.dispatch('guest/setReservations', []);
            this.$store.dispatch('guest/setReservation', null);
        },
        data() {
            return {
                busy: false,
                isViewRepeatGuests: false,
                totalRecords: 0,
                rowStyle: null,
                offset: 0,
                limit: 20,
                sort: 'id',
                desc: false,
                search: '',
                filter: { label: 'View All Guests', value: 'ALL_GUESTS' },
                currentSelectedRows: [],
                communicationActionsPermissions: {
                    push_permission: false,
                    email_permission: false,
                    sms_permission: false,
                },
            };
        },
        computed: {
            columnDefs() {
                return [
                    {
                        headerName: '#',
                        colId: 'number',
                        field: 'id',
                        pinned: 'left',
                        minWidth: 80,
                        maxWidth: 100,
                        sortable: false,
                        valueGetter: (params) => {
                            return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                        },
                        checkboxSelection: true,
                    },
                    {
                        field: 'short_name',
                        pinned: 'left',
                        width: isMediumScreen() ? 200 : 120,
                        autoWidthExclude: true,
                        headerName: 'Name',
                    },
                    {
                        field: 'id',
                        type: 'idColumn',
                        maxWidth: 100,
                        cellRenderer: ({ data, node }) => {
                            return node.rowPinned ? '' : data.id;
                        },
                    },
                    {
                        field: 'status',
                        headerName: 'Status',
                        cellRenderer: ({ data }) => {
                            let result = '<span class="d-flex align-items-center">';

                            if (data.status) {
                                if (data.status.is_vip) {
                                    result = `<img class="guest-status-image" title="VIP" src="/static/images/icons/reservation/vip_bg_24.svg"/>`;
                                } else if (data.status.is_blocklist) {
                                    result = `<img class="guest-status-image" title="Blocklist" src="/static/images/icons/reservation/blocklist_bg_24.svg"/>`;
                                } else {
                                    result = escapeHTML(data.status.name);
                                }
                            }

                            if (data.is_staylio) {
                                result += `<img class="guest-status-image" title="Staylio App" src="/static/images/staylio_app_icon.png"/>`;
                            }

                            result += '</span>';

                            return result;
                        },
                        cellClass: 'guest-status-cell',
                        maxWidth: 110,
                        minWidth: 110,
                    },
                    {
                        field: 'created',
                        headerName: 'Created',
                        minWidth: 120,
                    },
                    {
                        field: 'email',
                        headerName: 'Email',
                    },
                    {
                        field: 'company',
                        headerName: 'Company',
                        minWidth: 120,
                    },
                    {
                        field: 'phone',
                        headerName: 'Phone',
                        minWidth: 140,
                    },
                    {
                        field: 'mobile_phone',
                        headerName: 'Mobile',
                        minWidth: 140,
                    },
                    {
                        field: 'allow_sms',
                        headerName: 'SMS?',
                        minWidth: 80,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: ({ data }) => {
                            return data.allow_sms && data.mobile_phone
                                ? `<img width="20" height="20" title="SMS" src="/static/images/icons/reservation/sms_optin_bg_24.svg"/>`
                                : '';
                        },
                    },
                    {
                        field: 'type',
                        headerName: 'Type',
                    },
                    {
                        field: 'num_stays',
                        autoWidthExclude: true,
                        headerName: 'Stays',
                    },
                    {
                        headerName: '',
                        minWidth: 130,
                        resizable: false,
                        sortable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            refresh: () => {
                                this.refreshGrid();
                            },
                        },
                    },
                ];
            },
            dropdownFilterOptions() {
                const repeatFilter = { value: 'REPEAT_GUESTS', label: 'View Repeat Guests' };
                const allFilter = { value: 'ALL_GUESTS', label: 'View All Guests' };
                const blocklistFilter = { value: 'BLOCKLIST_GUESTS', label: 'View Blocklist Guests' };
                const vipFilter = { value: 'VIP_GUESTS', label: 'View Vip Guests' };
                const staylioFilter = { value: 'STAYLIO_GUESTS', label: 'View Staylio Guests' };

                return [
                    {
                        icon: ['fas', 'filter'],
                        label: repeatFilter.label,
                        active: this.filter === repeatFilter.value,
                        click: () => {
                            this.filter = repeatFilter;
                            this.$refs.grid.refreshDataSource();
                        },
                    },
                    {
                        icon: ['fas', 'filter'],
                        label: allFilter.label,
                        active: this.filter === allFilter.value,
                        click: () => {
                            this.filter = allFilter;
                            this.$refs.grid.refreshDataSource();
                        },
                    },
                    {
                        icon: ['fas', 'filter'],
                        label: blocklistFilter.label,
                        active: this.filter === blocklistFilter.value,
                        click: () => {
                            this.filter = blocklistFilter;
                            this.$refs.grid.refreshDataSource();
                        },
                    },
                    {
                        icon: ['fas', 'filter'],
                        label: vipFilter.label,
                        active: this.filter === vipFilter.value,
                        click: () => {
                            this.filter = vipFilter;
                            this.$refs.grid.refreshDataSource();
                        },
                    },
                    {
                        icon: ['fas', 'filter'],
                        label: staylioFilter.label,
                        active: this.filter === staylioFilter.value,
                        click: () => {
                            this.filter = staylioFilter;
                            this.$refs.grid.refreshDataSource();
                        },
                    },
                ];
            },
            actionOptions() {
                const options = [];
                if (this.communicationActionsPermissions.push_permission) {
                    options.push({
                        sectionLabel: 'Push Notify',
                    });
                    options.push({
                        icon: ['fas', 'caret-right'],
                        iconSize: 'sm',
                        label: this.hasSelectedRows ? 'Selected Guests' : 'All Guests',
                        active: false,
                        click: () => {
                            this.setNotifySelectedGuests(this.currentSelectedRows);
                            this.$router.push('/app/staylio-app/mass-push-notification/create/');
                        },
                    });
                    if (!this.hasSelectedRows) {
                        options.push({
                            icon: ['fas', 'caret-right'],
                            iconSize: 'sm',
                            label: 'Campaigns',
                            active: false,
                            click: () => {
                                this.$router.push('/app/staylio-app/mass-push-notification/');
                            },
                        });
                    }
                }

                if (this.communicationActionsPermissions.email_permission && !this.hasSelectedRows) {
                    options.push({
                        sectionLabel: 'Email',
                    });
                    options.push({
                        icon: ['fas', 'caret-right'],
                        iconSize: 'sm',
                        label: 'All Guests',
                        active: false,
                        click: () => {
                            this.$router.push('/app/emarketing/setup/');
                        },
                    });
                }

                if (this.communicationActionsPermissions.sms_permission && !this.hasSelectedRows) {
                    options.push({
                        sectionLabel: 'SMS',
                    });
                    options.push({
                        icon: ['fas', 'caret-right'],
                        iconSize: 'sm',
                        label: 'All Guests',
                        active: false,
                        click: () => {
                            this.$router.push('/app/sms/send-message/');
                        },
                    });
                }

                return options;
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    return API.get(
                        encodeURI(
                            `/app/guest/guest/?offset=${offset}&limit=${limit}&sort=${sort}&desc=${desc}&search=${search}&gfilter=${this.filter.value}`
                        )
                    );
                };
            },
            exportExcelHref() {
                return `/app/guest/guest/excel/?offset=${this.offset}&limit=${this.limit}&sort=${this.sort}&desc=${this.desc}&search=${this.search}&gfilter=${this.filter.value}`;
            },
            pinnedBottomRowData() {
                return [
                    {
                        id: this.totalRecords,
                    },
                ];
            },
            isMobile() {
                const md = 768;
                return window.innerWidth < md || window.innerHeight < md;
            },
            hasSelectedRows() {
                return this.currentSelectedRows.length > 0;
            },
        },
        methods: {
            ...mapMutations('guest', ['setNotifySelectedGuests']),

            refreshGrid() {
                this.$refs.grid.refreshDataSource();
            },
            setTotalRecords(value) {
                this.totalRecords = value;
                this.$refs.grid.gridOptions.api.setGridOption('pinnedBottomRowData', this.pinnedBottomRowData);
            },
            handleSelectionChange() {
                const gridApi = this.$refs.grid.gridOptions.api;
                const rows = gridApi.getSelectedRows();
                this.currentSelectedRows = rows.map((row) => {
                    return {
                        value: row.id,
                        text: row.short_name,
                    };
                });
                this.currentFeedShare = null;
            },
            clearSelection() {
                this.$refs.grid.gridOptions.api.deselectAll();
                this.currentSelectedRows = [];
                this.setNotifySelectedGuests([]);
            },
            async fetchCommunicationActionPermissions() {
                const response = (await API.get(encodeURI(`/app/guest/guest/communication-actions/`))).data;
                this.communicationActionsPermissions = response;
            },
        },
    };
</script>
<style scoped lang="scss">
    :deep(.guest-status-cell) {
        .guest-status-image {
            width: 25px;
            height: 25px;
            margin-right: 0.5rem;
        }
    }
</style>
